/**
 * Experiment-arm snapshot for the generic `xa` buy-link stamp
 *
 * Module-level map of flag → resolved arm for the current visitor, so pure
 * helpers like `enrichRedirectUrl` / `convertToRedirectUrl` can stamp
 * `xa=<flag>:<variant>,…` onto redirect URLs — and therefore
 * `link_clicks.metadata.exp_arms` — without threading arms through every call
 * site. The generic form of `signupWallVariantSnapshot`: the component that
 * evaluates a flag (and only for its eligible population) publishes the arm,
 * so stamping never adds a flag evaluation of its own and never pollutes the
 * experiment's exposures. Only flags in `EXPERIMENT_ARM_ALLOWLIST` are stamped.
 *
 * BROWSER-ONLY: never call `setExperimentArmSnapshot` server-side. Node module
 * singletons are shared across concurrent SSR requests, so a server write
 * would leak one visitor's arm onto every in-flight response. Publish from a
 * client effect.
 *
 * Like the sign-up wall snapshot, it is empty during SSR, so a memoized `href`
 * can lack `xa` until a client re-render. Clicks happen after hydration, so the
 * window is near zero.
 */
import {
  type ExperimentArmFlag,
  serializeExperimentArms,
} from '@dupe/common/experimentArms'

let arms: Partial<Record<ExperimentArmFlag, string>> = {}

/** Publish (or clear, with null) one flag's resolved arm. */
export const setExperimentArmSnapshot = (
  flag: ExperimentArmFlag,
  variant: string | null
): void => {
  if (variant === null) {
    const { [flag]: _cleared, ...rest } = arms
    arms = rest
  } else {
    arms = { ...arms, [flag]: variant }
  }
}

/** The `xa` value for the arms published so far, or null when none. */
export const getExperimentArmsParam = (): string | null =>
  serializeExperimentArms(arms)

/** Test helper — reset the module-level state between tests. */
export const resetExperimentArmSnapshot = (): void => {
  arms = {}
}
